import { useCallback, useEffect, useRef, useState } from "react";

import {
  Classes,
  Intent,
  NumericInput,
  OverlayToaster,
  Portal,
} from "@blueprintjs/core";
import styled from "@emotion/styled";
import debounce from "lodash/debounce";
import { useMediaQuery } from "react-responsive";
import Select from "react-select";

import { Box, Flex } from "@/components/layout/flexbox";
import { breakpoints, isXsToLgQuery } from "@/components/layout/Media";
import { SlideContainer } from "@/components/layout/SlideContainer";
import { CARD_CLASSES, SUBTEXT_COLOR } from "@/css/constants";
import {
  BufferSettingsDocument,
  useBufferSettingsQuery,
  useUpdateCoachBufferSettingsMutation,
} from "@/graphql";
import { convertUnit } from "@/utils/datetime";

interface BufferSettings {
  bufferBefore: number;
  bufferAfter: number;
  minuteIncrement: number;
}
type BufferSettingsOption = keyof BufferSettings;

export const BUFFER_OPTIONS = [
  {
    label: "0 mins",
    value: 0,
  },
  {
    label: "5 mins",
    value: 5,
  },
  {
    label: "10 mins",
    value: 10,
  },
  {
    label: "15 mins",
    value: 15,
  },
  {
    label: "30 mins",
    value: 30,
  },
];

export const MINUTE_INCREMENT_OPTIONS = [
  {
    label: "15 mins",
    value: 15,
  },
  {
    label: "30 mins",
    value: 30,
  },
  {
    label: "60 mins",
    value: 60,
  },
];

export const UNIT_OPTIONS = [
  {
    label: "Minutes",
    value: "minutes",
  },
  {
    label: "Hours",
    value: "hours",
  },
  {
    label: "Days",
    value: "days",
  },
];

export const SCHEDULE_SETTING_FIELD: {
  name: BufferSettingsOption;
  title: string;
  description: string;
}[] = [
  {
    name: "bufferBefore",
    title: " Buffer time before your sessions",
    description: "Use this field to give yourself space between sessions",
  },
  {
    name: "bufferAfter",
    title: "Buffer time after your sessions",
    description: "Use this field to give yourself space between sessions",
  },
  {
    name: "minuteIncrement",
    title: "Session start times in increments of....",
    description: "Set the frequency of available time slots",
  },
];

interface MinimumMeetingNoticeSettingProps {
  isLoading: boolean;
  isXsToLg: boolean;
  minimumMeetingNotice: { value: number; unit: string };
  onChangeMinimumMeetingNotice: (minimumMeetingNotice: {
    value: number;
    unit: string;
  }) => void;
}

function MinimumMeetingNoticeSetting({
  isLoading,
  isXsToLg,
  minimumMeetingNotice,
  onChangeMinimumMeetingNotice,
}: MinimumMeetingNoticeSettingProps) {
  const handleChangeMinimumMeetingNoticeValue = useCallback(
    (value: number) => {
      onChangeMinimumMeetingNotice({
        value: value,
        unit: minimumMeetingNotice.unit,
      });
    },
    [onChangeMinimumMeetingNotice, minimumMeetingNotice.unit],
  );

  const handleChangeMinimumMeetingNoticeUnit = useCallback(
    ({ value: unit }: { value: string }) => {
      let newValue = convertUnit(
        minimumMeetingNotice.value,
        minimumMeetingNotice.unit,
        unit,
      );
      if (unit === "minutes") {
        newValue = Math.round(newValue);
      }
      onChangeMinimumMeetingNotice({
        value: newValue,
        unit: unit,
      });
    },
    [
      onChangeMinimumMeetingNotice,
      minimumMeetingNotice.value,
      minimumMeetingNotice.unit,
    ],
  );

  return (
    <Flex
      alignItems={isXsToLg ? "flex-start" : "flex-end"}
      justifyContent="space-between"
      mt={4}
      flexDirection={isXsToLg ? "column" : "row"}
    >
      <Box>
        <Box fontSize={16} fontWeight={600}>
          Minimum meeting notice
        </Box>
        <Box color={SUBTEXT_COLOR}>
          Set much notice you need for a new meeting
        </Box>
      </Box>
      <Flex className="minimum-meeting-notice-container">
        <NumericInput
          disabled={isLoading}
          placeholder="Enter number"
          onValueChange={handleChangeMinimumMeetingNoticeValue}
          value={minimumMeetingNotice.value}
          buttonPosition="none"
          className="minimum-meeting-notice-input bas round-corners"
        />
        <Select
          isLoading={isLoading}
          options={UNIT_OPTIONS}
          className="select-time-range bas round-corners"
          onChange={(option) =>
            option && handleChangeMinimumMeetingNoticeUnit(option)
          }
          value={UNIT_OPTIONS.find(
            (item) => item.value === minimumMeetingNotice.unit,
          )}
        />
      </Flex>
    </Flex>
  );
}

const calculateMinimumMeetingNotice = (value: number) => {
  if (value == 0) {
    return {
      value: 0,
      unit: "minutes",
    };
  }
  const days = (value / (60 * 24)).toFixed(1);
  if (days[0] !== "0" && days[days.length - 1] === "0") {
    return {
      value: Number(days),
      unit: "days",
    };
  }
  const hours = (value / 60).toFixed(1);
  if (hours[0] !== "0" && hours[hours.length - 1] === "0") {
    return {
      value: Number(hours),
      unit: "hours",
    };
  }
  return {
    value: value,
    unit: "minutes",
  };
};

interface ScheduleSettingsProps {
  onSettingChanged: () => void;
}

export function ScheduleSettings({ onSettingChanged }: ScheduleSettingsProps) {
  const [bufferSettings, setBufferSettings] = useState<BufferSettings>({
    bufferBefore: 10,
    bufferAfter: 10,
    minuteIncrement: 30,
  });

  const [bufferOptions] = useState({
    bufferBefore: BUFFER_OPTIONS,
    bufferAfter: BUFFER_OPTIONS,
    minuteIncrement: MINUTE_INCREMENT_OPTIONS,
  });

  const [minimumMeetingNotice, setMinimumMeetingNotice] = useState({
    value: 6,
    unit: "hours",
  });

  const { data, loading } = useBufferSettingsQuery();

  // prevent mounted state errors in dev console
  useEffect(() => {
    if (data?.currentUser?.agentProfile) {
      setBufferSettings({
        bufferBefore: data.currentUser.agentProfile.bufferBefore || 10,
        bufferAfter: data.currentUser.agentProfile.bufferAfter || 10,
        minuteIncrement: data.currentUser.agentProfile.minuteIncrement || 30,
      });

      const minimumMeetingNotice = calculateMinimumMeetingNotice(
        data.currentUser.agentProfile.minimumMeetingNotice || 1440, // 1440 is 1 day in minutes
      );

      setMinimumMeetingNotice(minimumMeetingNotice);
    }
  }, [data]);

  const [savedVisible, setSavedVisible] = useState(false);

  const [updateBufferSettings] = useUpdateCoachBufferSettingsMutation({
    refetchQueries: [{ query: BufferSettingsDocument }],
    awaitRefetchQueries: true,
    onCompleted: () => {
      setSavedVisible(true);
      if (onSettingChanged) {
        onSettingChanged();
      }
      setTimeout(() => {
        setSavedVisible(false);
      }, 3000);
    },
  });

  const isXsToLg = useMediaQuery(isXsToLgQuery);

  const onChangeOption = (
    option: { value: number },
    field: keyof BufferSettings,
  ) => {
    let newBufferSettings: BufferSettings = { ...bufferSettings };
    newBufferSettings[field] = option.value;
    setBufferSettings(newBufferSettings);

    showToaster();

    updateBufferSettings({
      variables: {
        ...newBufferSettings,
        minimumMeetingNotice: Math.round(
          convertUnit(
            minimumMeetingNotice.value,
            minimumMeetingNotice.unit,
            "minutes",
          ),
        ),
      },
    });
  };

  const updateBufferSettingsWithDebounce = useCallback(
    debounce((value) => {
      showToaster();
      updateBufferSettings(value);
    }, 3000),
    [updateBufferSettings],
  );

  function showToaster() {
    toaster.current?.show({
      message: "Buffer Settings Saved",
      intent: Intent.SUCCESS,
    });
  }

  const handleChangeMinimumMeetingNotice = useCallback(
    (minimumMeetingNotice: { value: number; unit: string }) => {
      setMinimumMeetingNotice(minimumMeetingNotice);
      updateBufferSettingsWithDebounce({
        variables: {
          ...bufferSettings,
          minimumMeetingNotice: Math.round(
            convertUnit(
              minimumMeetingNotice.value,
              minimumMeetingNotice.unit,
              "minutes",
            ),
          ),
        },
      });
    },
    [setMinimumMeetingNotice, updateBufferSettingsWithDebounce, bufferSettings],
  );

  const toaster = useRef<OverlayToaster>(null);

  return (
    <SelectBox
      mt={4}
      className={CARD_CLASSES}
      backgroundColor="#FFF"
      justifyContent="space-between"
      alignItems="center"
      py={[3, 3, 3, 24]}
      px={[3, 3, 3, 4]}
    >
      <Box fontSize={20} fontWeight={600}>
        Schedule Settings
      </Box>
      <Box color={SUBTEXT_COLOR}>Control your availability</Box>
      <MinimumMeetingNoticeSetting
        isLoading={loading}
        isXsToLg={isXsToLg}
        minimumMeetingNotice={minimumMeetingNotice}
        onChangeMinimumMeetingNotice={handleChangeMinimumMeetingNotice}
      />
      {SCHEDULE_SETTING_FIELD.map((field, index) => (
        <Flex
          alignItems={isXsToLg ? "flex-start" : "flex-end"}
          justifyContent="space-between"
          mt={4}
          flexDirection={isXsToLg ? "column" : "row"}
          key={index}
        >
          <Box>
            <Box fontSize={16} fontWeight={600}>
              {field.title}
            </Box>
            <Box color={SUBTEXT_COLOR}>{field.description}</Box>
          </Box>
          <Select
            isLoading={loading}
            options={bufferOptions[field.name]}
            className="select-time-range bas round-corners"
            onChange={(option) => {
              if (option) {
                onChangeOption(option, field.name);
              }
            }}
            value={bufferOptions[field.name].find(
              (item) => item.value === bufferSettings[field.name],
            )}
          />
        </Flex>
      ))}
      <Portal>
        <SlideContainer
          visible={savedVisible}
          slideFrom="DOWN"
          style={{
            position: "fixed",
            top: undefined,
            bottom: 15,
            left: undefined,
            width: 385,
          }}
        >
          <OverlayToaster ref={toaster} position="bottom" />
        </SlideContainer>
      </Portal>
    </SelectBox>
  );
}

const SelectBox = styled(Box)`
  .select-time-range {
    width: 138px;
    height fit-content;
    border: 1px solid #E2E2E2;

    @media screen and (max-width: ${breakpoints.lg - 1}px) {
      width: 100%;
      margin-top: 10px;
    }

    [class$='-control'] {
      border: none;
      border-radius: 6px;
      padding: 9px 4px;
      color: ${SUBTEXT_COLOR};
    }
  }
  .minimum-meeting-notice-input {
    margin-right: 10px;
    padding-right: 1px;
    .${Classes.INPUT_GROUP} {
      width: 100%
    }
    input {
      height: 100%;
      width: 70px;
      border: none;
      box-shadow: none;
      text-align: center;
    }

    @media screen and (max-width: ${breakpoints.lg - 1}px) {
      margin-top: 10px;
      input {
        width: 100%;
      }
    }
  }

  .minimum-meeting-notice-container {
    @media screen and (max-width: ${breakpoints.lg - 1}px) {
      width: 100%;
      .select-time-range {
        width: 50%;
      }
    }
  }
`;
